import { BrowserAuthError, ClientAuthError } from '@azure/msal-browser';
import axios from 'axios';

/**
 * Error response of the API (RFC 7807 ProblemDetails, see Hellang.Middleware.ProblemDetails).
 */
export interface IProblemDetails {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  // AnalyticsException.Code, e.g. LOCATION_NOT_MAPPED
  code?: string;
  // validation errors: field name -> messages
  errors?: Record<string, string[] | string>;
}

const STATUS_MESSAGES: Record<number, string> = {
  400: 'The request is invalid.',
  403: 'You do not have permission to perform this action.',
  404: 'The requested resource was not found.',
  409: 'The data was changed by someone else. Please reload the page and try again.',
  500: 'Something went wrong on the server. Please try again later.',
  502: 'The server is temporarily unavailable. Please try again later.',
  503: 'The server is temporarily unavailable. Please try again later.',
  504: 'The server did not respond in time. Please try again later.',
};

const NETWORK_ERROR_MESSAGE = 'Unable to connect to the server. Please check your network connection and try again.';
const TIMEOUT_MESSAGE = 'The request timed out. Please try again.';
const UNKNOWN_ERROR_MESSAGE = 'An unexpected error occurred. Please try again.';

// titles generated by ASP.NET Core / the problem details middleware do not tell the user anything useful
const GENERIC_TITLES = [
  'One or more validation errors occurred.',
  'An error occurred while processing your request.',
  'Bad Request',
  'Unauthorized',
  'Forbidden',
  'Not Found',
  'Conflict',
  'Internal Server Error',
];

const getProblemDetails = (err: unknown): IProblemDetails | undefined => {
  if (!axios.isAxiosError(err)) {
    return undefined;
  }

  const data = err.response?.data;
  return data && typeof data === 'object' ? (data as IProblemDetails) : undefined;
};

/**
 * Errors which must not be shown to the user: cancelled requests, authentication handled by MSAL.
 */
export const isSilentError = (err: unknown): boolean => {
  if (err instanceof BrowserAuthError) {
    return true;
  }

  if (axios.isCancel(err)) {
    return true;
  }

  return axios.isAxiosError(err) && (err.code === 'ERR_CANCELED' || err.response?.status === 401);
};

/**
 * Error code of AnalyticsException (ProblemDetails 'code' extension).
 */
export const getErrorCode = (err: unknown): string | undefined => getProblemDetails(err)?.code;

/**
 * User friendly messages of an API or authentication error.
 */
export const getErrorMessages = (err: unknown): string[] => {
  if (err instanceof ClientAuthError && err.message?.startsWith('endpoints_resolution_error')) {
    return [NETWORK_ERROR_MESSAGE];
  }

  if (!axios.isAxiosError(err)) {
    return [err instanceof Error && err.message ? err.message : UNKNOWN_ERROR_MESSAGE];
  }

  if (err.code === 'ECONNABORTED' || err.code === 'ETIMEDOUT') {
    return [TIMEOUT_MESSAGE];
  }

  if (!err.response) {
    return [NETWORK_ERROR_MESSAGE];
  }

  const problem = getProblemDetails(err);

  // business errors of the API (AnalyticsException): 400 with an error code, 'detail' is the message for the user
  if (err.response.status === 400 && problem?.code && problem.detail) {
    return [problem.detail];
  }

  const validationMessages = Object.values(problem?.errors ?? {})
    .flatMap((x) => (Array.isArray(x) ? x : [x]))
    .filter((x): x is string => !!x);
  if (validationMessages.length) {
    return Array.from(new Set(validationMessages));
  }

  if (problem?.detail) {
    return [problem.detail];
  }

  if (problem?.title && !GENERIC_TITLES.includes(problem.title)) {
    return [problem.title];
  }

  const status = err.response.status;
  return [STATUS_MESSAGES[status] ?? (status >= 500 ? STATUS_MESSAGES[500] : UNKNOWN_ERROR_MESSAGE)];
};

/**
 * All error messages joined into one text, e.g. for an inline error block.
 */
export const getErrorMessage = (err: unknown): string => getErrorMessages(err).join(' ');
